import { useEffect, useState } from "react";
import { apiClient } from "../App";
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { UserPlus, Loader2 } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["to_be_contacted", "contacted", "invited", "archived"];
const STATUS_LABEL = {
  to_be_contacted: "To be contacted", contacted: "Contacted", invited: "Invited", archived: "Archived",
};

export const ReferralQueue = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState({});

  const load = async () => {
    setLoading(true);
    try { const res = await apiClient.get("/fundraiser-referrals"); setRows(res.data || []); }
    catch (e) { /* non-fatal */ } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const save = async (id, patch) => {
    try {
      await apiClient.put(`/fundraiser-referrals/${id}`, patch);
      toast.success("Referral updated");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };

  return (
    <Card className="border-slate-200 shadow-none" data-testid="referral-queue">
      <CardContent className="p-4">
        <div className="flex items-center gap-2 mb-3">
          <UserPlus className="h-5 w-5 text-emerald-600" />
          <h3 className="font-heading font-bold text-slate-900">Fundraiser Referral Queue</h3>
        </div>
        {loading ? (
          <div className="text-center py-6 text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
        ) : rows.length === 0 ? (
          <div className="text-center py-6 text-slate-400 text-sm" data-testid="no-queue-referrals">No referrals yet.</div>
        ) : (
          <div className="space-y-3">
            {rows.map((r) => {
              const e = editing[r.id] || {};
              const val = (k, d) => (e[k] !== undefined ? e[k] : (r[k] ?? d));
              const setE = (k, v) => setEditing({ ...editing, [r.id]: { ...e, [k]: v } });
              return (
                <div key={r.id} className="p-3 border border-slate-200 rounded-lg" data-testid={`queue-referral-${r.id}`}>
                  <div className="flex items-center justify-between flex-wrap gap-2">
                    <div>
                      <span className="font-semibold text-slate-800">{r.firstName} {r.lastName}</span>
                      <span className="text-xs text-slate-400 ml-2">{r.phone} · {r.email}</span>
                      <p className="text-xs text-slate-500">Referred by {r.referringFundraiserName} · {r.submittedAt ? new Date(r.submittedAt).toLocaleDateString("en-GB") : ""}</p>
                      {r.notes && <p className="text-xs text-slate-600 mt-1">"{r.notes}"</p>}
                    </div>
                    <Select value={val("status")} onValueChange={(v) => save(r.id, { status: v, ...(v === "invited" ? { invitedUserId: r.invitedUserId || null } : {}) })}>
                      <SelectTrigger className="w-40 h-8 text-xs" data-testid={`queue-status-${r.id}`}><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUSES.map((s) => <SelectItem key={s} value={s}>{STATUS_LABEL[s]}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <Input className="h-8 text-xs" placeholder="Bonus £" type="number" defaultValue={r.bonusAmount ?? ""} onBlur={(ev) => ev.target.value !== String(r.bonusAmount ?? "") && save(r.id, { bonusAmount: ev.target.value })} data-testid={`queue-bonus-${r.id}`} />
                    <Input className="h-8 text-xs" placeholder="Bonus conditions" defaultValue={r.bonusConditions ?? ""} onBlur={(ev) => ev.target.value !== (r.bonusConditions ?? "") && save(r.id, { bonusConditions: ev.target.value })} data-testid={`queue-bonus-cond-${r.id}`} />
                  </div>
                  <Textarea className="text-xs mt-2" rows={1} placeholder="Manager notes" defaultValue={r.managerNotes ?? ""} onBlur={(ev) => ev.target.value !== (r.managerNotes ?? "") && save(r.id, { managerNotes: ev.target.value })} data-testid={`queue-notes-${r.id}`} />
                  <label className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                    <input type="checkbox" defaultChecked={!!r.managerNotesVisible} onChange={(ev) => save(r.id, { managerNotesVisible: ev.target.checked })} data-testid={`queue-notes-visible-${r.id}`} />
                    Notes visible to fundraiser
                  </label>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
